import type { OrderLine } from '../../shared/orderStatus';
import { StepIndicator } from './StepIndicator';
import { daysUntil } from '../utils/time';

interface CoreTrackerProps {
  line: OrderLine;
  coreChargeDeferred?: boolean;
}

export function CoreTracker({ line, coreChargeDeferred }: CoreTrackerProps) {
  const firstStep = line.steps[0];
  const lastStep = line.steps[line.steps.length - 1];
  const expirationDate = firstStep?.details?.expirationDate;
  const coreBalance = firstStep?.details?.coreBalance;
  const isAccepted = lastStep?.name === 'Core Accepted' && lastStep.status === 'completed';
  const isRejected = lastStep?.name === 'Core Rejected' && lastStep.status === 'completed';

  return (
    <div className="ml-4 mt-3 border-l-2 border-dashed border-amber-300 pl-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <svg
            className="w-4 h-4 text-amber-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
            Core Return
          </span>
        </div>

        {isAccepted && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 font-semibold">
            ACCEPTED
          </span>
        )}
        {isRejected && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">
            REJECTED
          </span>
        )}

        {expirationDate && !isAccepted && !isRejected && (
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              daysUntil(expirationDate) <= 7
                ? 'bg-red-100 text-red-700'
                : daysUntil(expirationDate) <= 14
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-500'
            }`}
          >
            {daysUntil(expirationDate) > 0
              ? `${daysUntil(expirationDate)}d to return`
              : 'OVERDUE'}
          </span>
        )}

        {coreBalance !== undefined && (
          <span className="text-[10px] text-slate-400">${coreBalance.toFixed(2)} core charge</span>
        )}
      </div>

      {coreChargeDeferred && (
        <p className="text-[11px] text-slate-500 mb-2 italic">
          Core charge is deferred — you&rsquo;ll only be charged if the core is rejected or not
          returned by the deadline.
        </p>
      )}

      <div>
        {line.steps.map((step, i) => (
          <StepIndicator
            key={step.sequence}
            step={step}
            isLast={i === line.steps.length - 1}
            isCore
          />
        ))}
      </div>
    </div>
  );
}
